import React, { useCallback } from 'react'
import styled from 'styled-components'

import { ButtonContainer, PageTitle, screenSize } from 'styles/shared'
import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'

type props = React.PropsWithChildren<{
  title?: string
  deleteButtonText?: string
  isSaving: boolean
  saveDisabled?: boolean
  isDeleting?: boolean
  onSaveCallback: () => void
  onDeleteCallback?: () => void
  onCancelCallback: () => void
  onPreviewCallback?: () => void
  displayDeleteButton?: boolean
  previewButtonText?: string
  fullScreen?: boolean
  className?: string
  displayPreviewButton?: boolean
  noBorder?: boolean
}>

const Form = ({
  title,
  deleteButtonText,
  isSaving = false,
  isDeleting = false,
  saveDisabled,
  onSaveCallback,
  onDeleteCallback = () => { },
  onCancelCallback,
  onPreviewCallback = () => {},
  children,
  displayDeleteButton = false,
  fullScreen = false,
  className = '',
  displayPreviewButton = false,
  previewButtonText = '',
  noBorder = false
}: props) => {
  const onClickSave = useCallback((event) => {
    event.preventDefault()
    onSaveCallback()
  }, [onSaveCallback])

  const onClickCancel = useCallback((event) => {
    event.preventDefault()

    onCancelCallback()
  }, [onCancelCallback])

  const onClickDelete = useCallback((event) => {
    event.preventDefault()

    onDeleteCallback()
  }, [onDeleteCallback])

  return (
    <S.Form {...{ className }} fullScreen={fullScreen}>
      {title && <S.PageTitle>{title}</S.PageTitle> }

      <S.Fieldset>
        {children}
      </S.Fieldset>

      <S.ButtonContainer displayPreviewButton={displayPreviewButton} variant={displayDeleteButton ? 'submit-delete' : 'submit'} noBorder={noBorder}>
        {displayDeleteButton && <Button decoration="danger" enabled={!isDeleting} text={`Delete ${deleteButtonText}`} onPress={onClickDelete} showSpinner={isDeleting} />}

        <S.RightSide>
          {displayPreviewButton && <Button decoration="secondary" text={previewButtonText} styles={secondaryButtonStyle} onPress={onPreviewCallback} />}
          <Button decoration="secondary" enabled={!saveDisabled} text="Cancel" styles={secondaryButtonStyle} onPress={onClickCancel} />
          <Button decoration="positive" enabled={saveDisabled ? !saveDisabled : !isSaving} text="Save" onPress={onClickSave} showSpinner={isSaving} />
        </S.RightSide>
      </S.ButtonContainer>
    </S.Form>
  )
}

export default Form

const secondaryButtonStyle: ButtonStyles = {
  button: {
    marginRight: 15
  }
}

const S = {
  Form: styled.form.withConfig({
    shouldForwardProp: (prop) => !['fullScreen'].includes(prop)
  })<{ fullScreen: boolean, children: React.ReactNode }>`
    max-width: ${({ fullScreen }) => fullScreen ? '100%' : '800px'};
  `,
  ButtonContainer: styled(ButtonContainer).withConfig({
    shouldForwardProp: (prop) => !['displayPreviewButton'].includes(prop)
  })<{ displayPreviewButton: boolean }>`
    & > button > div {
      text-transform: capitalize !important; // TODO: need to fix this in DWA-6533
    }
    ${({ displayPreviewButton }) => displayPreviewButton &&
    `@media ${screenSize.small} {
        display: grid;
        gap: 10px;
        margin-bottom: 50px;
        justify-content: center;
      }`
}

  `,
  Fieldset: styled.fieldset`
    border: none;
    padding: 10px 10px 0px 10px;
    margin: 0 2px;
  `,
  PageTitle: styled(PageTitle)`
    margin-top: revert;

    @media ${screenSize.small} {
      display: block;
    }
  `,
  RightSide: styled.div`
    display: flex;
    
    & > button > div {
      text-transform: capitalize !important; // TODO: need to fix this in DWA-6533
    }
  `
}
